<template>
  <form
    @submit.prevent="submit"
    class="contract-revision-form uk-form-stacked uk-grid-small"
  >
    <h2 class="uk-margin-medium">
      <router-link
        :to="{ name: 'contract', params: { id: contract.id } }"
      >{{contract.name}}</router-link>
    </h2>

    <!-- Validity -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.validity')}}</legend>
      <div class="uk-grid-small" uk-grid>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.validFrom"
            :label="$t('fields.validFrom')"
            :max="fields.values.validUntil"
            placeholder="∞"
            v-model="fields.values.validFrom"
          />
        </div>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.validUntil"
            :label="$t('fields.validUntil')"
            :min="fields.values.validFrom"
            placeholder="∞"
            v-model="fields.values.validUntil"
          />
        </div>
      </div>
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.details')}}</legend>

      <duration-input
        :disabled="!isEditable"
        :errors="fields.errors.contractDuration"
        :label="$t('fields.contractDuration')"
        :placeholder="inheritedContractDurationPlaceholder"
        v-model="fields.values.contractDuration"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.usps"
        :label="$t('fields.usps')"
        :placeholder="contract.usps"
        v-model="fields.values.usps"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.summaryTexts"
        :label="$t('fields.summaryTexts')"
        :placeholder="contract.summaryTexts"
        v-model="fields.values.summaryTexts"
      />
    </fieldset>

    <!-- Legal Texts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.legalTexts')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.legalTexts"
      >
        <legal-text-manager
          :disabled="!isEditable"
          :errors="fields.errors.legalTexts"
          v-model="fields.values.legalTexts"
        />
        <legal-text-manager
          :has-controls="false"
          :value="contract.legalTexts"
          slot="inherited"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- SEO -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.seo')}}</legend>

      <input-inheritance-toggle
        :label="$t('fields.keywords')"
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.keywords"
      >
        <keyword-list-input
          :disabled="!isEditable"
          :errors="fields.errors.keywords"
          :label="$t('fields.keywords')"
          v-model="fields.values.keywords"
        />
        <div slot="inherited">
          <separated-list :items="contract.keywords" />
        </div>
      </input-inheritance-toggle>
    </fieldset>

    <!-- Media -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.media')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        :value="fields.values.media"
        @change="fields.change.media"
        toggle-label="Überschreiben"
      >
        <attachment-manager
          :disabled="!isEditable"
          :errors="fields.errors.media"
          :value="fields.values.media"
          @change="fields.change.media"
          attachment-type="MEDIA_OBJECT"
        />
        <attachment-manager
          :has-controls="false"
          :value="contract.media"
          slot="inherited"
        />
      </inheritance-toggle>
    </fieldset>

    <div class="uk-margin-medium-top">
      <submit-button :disabled="!isSubmittable" type="primary" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import AttachmentManager from '@/components/AttachmentManager';
import CancelButton from '@/components/CancelButton';
import DateTimePicker from '@/components/DateTimePicker';
import DurationInput from '@/components/DurationInput';
import Form from '@/mixins/Form';
import InheritanceToggle from '@/components/InheritanceToggle';
import InputInheritanceToggle from '@/components/InputInheritanceToggle';
import KeywordListInput from '@/components/KeywordListInput';
import LegalTextManager from '@/components/LegalTextManager';
import SeparatedList from '@/components/SeparatedList';
import SubmitButton from '@/components/SubmitButton';
import TextInput from '@/components/TextInput';
import TextListInput from '@/components/TextListInput';

export default {
  name: 'contract-revision-form',
  mixins: [
    Form({
      contractDuration: { type: Form.Types.NullableObject, default: null },
      keywords: { type: Form.Types.NullableArray },
      legalTexts: { type: Form.Types.ReferenceList, default: null },
      media: {
        type: Form.Types.ReferenceList,
        default: null,
        dumpTo: 'mediaObjectIds',
      },
      summaryTexts: { type: Form.Types.NullableArray },
      usps: { type: Form.Types.NullableArray },
      validFrom: true,
      validUntil: true,
    }),
  ],
  components: {
    AttachmentManager,
    CancelButton,
    DateTimePicker,
    DurationInput,
    InheritanceToggle,
    InputInheritanceToggle,
    KeywordListInput,
    LegalTextManager,
    SeparatedList,
    SubmitButton,
    TextInput,
    TextListInput,
  },
  props: {
    contract: {
      type: Object,
      required: true,
    },
  },
  computed: {
    inheritedContractDurationPlaceholder() {
      if (!this.contract.contractDuration) return null;
      return this.contract.contractDuration.value.toString();
    },
  },
};
</script>



// WEBPACK FOOTER //
// ContractRevisionForm.vue?adbc887c